import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react";

import type { MockConversation, MockConversationSnippet } from "./MockTranscriptEventsTab.helpers";

export const BASE_INTERVAL_MS = 3000;

export type PlaybackSpeed = 1 | 2 | 4;

export type PlaybackState =
    | { kind: "idle" }
    | {
          kind: "playing";
          conversation: MockConversation;
          sentIndex: number;
          speed: PlaybackSpeed;
      }
    | {
          kind: "paused";
          conversation: MockConversation;
          sentIndex: number;
          remainingMs: number;
          speed: PlaybackSpeed;
      };

interface UseMockPlaybackArgs {
    sendSnippet: (snippet: MockConversationSnippet) => Promise<unknown>;
    onError?: (error: Error) => void;
}

export interface UseMockPlaybackResult {
    state: PlaybackState;
    play: (conversation: MockConversation, speed: PlaybackSpeed) => void;
    pause: () => void;
    resume: () => void;
    stop: () => void;
    setSpeed: (speed: PlaybackSpeed) => void;
}

type TimerHandle = ReturnType<typeof setTimeout>;

export function useMockPlayback({ sendSnippet, onError }: UseMockPlaybackArgs): UseMockPlaybackResult {
    const [state, setState] = useState<PlaybackState>({ kind: "idle" });
    const timerRef = useRef<TimerHandle | null>(null);
    const nextFireAtRef = useRef<number | null>(null);

    const handleSendSnippet = useEffectEvent((snippet: MockConversationSnippet) => sendSnippet(snippet));
    const handleError = useEffectEvent((error: Error) => onError?.(error));

    const clearTimer = () => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        nextFireAtRef.current = null;
    };

    const scheduleTimer = (delayMs: number, callback: () => void) => {
        nextFireAtRef.current = Date.now() + delayMs;
        timerRef.current = setTimeout(callback, delayMs);
    };

    useEffect(() => {
        return () => clearTimer();
    }, []);

    const fire = useCallback((conversation: MockConversation, indexToSend: number, speed: PlaybackSpeed) => {
        const snippet = conversation.snippets[indexToSend];
        if (!snippet) return;

        // oxlint-disable-next-line react-hooks/rules-of-hooks
        void handleSendSnippet(snippet).catch((error: Error) => {
            clearTimer();
            setState({ kind: "idle" });
            // oxlint-disable-next-line react-hooks/rules-of-hooks
            handleError(error);
        });

        setState({ kind: "playing", conversation, sentIndex: indexToSend, speed });

        const intervalMs = BASE_INTERVAL_MS / speed;
        const nextIndex = indexToSend + 1;
        if (nextIndex >= conversation.snippets.length) {
            scheduleTimer(intervalMs, () => {
                clearTimer();
                setState({ kind: "idle" });
            });
            return;
        }

        scheduleTimer(intervalMs, () => fire(conversation, nextIndex, speed));
    }, []);

    const play = useCallback(
        (conversation: MockConversation, speed: PlaybackSpeed) => {
            clearTimer();
            fire(conversation, 0, speed);
        },
        [fire],
    );

    const pause = useCallback(() => {
        setState((prev) => {
            if (prev.kind !== "playing") return prev;
            const remainingMs = Math.max(0, (nextFireAtRef.current ?? Date.now()) - Date.now());
            clearTimer();
            return {
                kind: "paused",
                conversation: prev.conversation,
                sentIndex: prev.sentIndex,
                remainingMs,
                speed: prev.speed,
            };
        });
    }, []);

    const resume = useCallback(() => {
        setState((prev) => {
            if (prev.kind !== "paused") return prev;
            const { conversation, sentIndex, remainingMs, speed } = prev;
            const nextIndex = sentIndex + 1;
            if (nextIndex >= conversation.snippets.length) {
                return { kind: "idle" };
            }
            scheduleTimer(remainingMs, () => fire(conversation, nextIndex, speed));
            return { kind: "playing", conversation, sentIndex, speed };
        });
    }, [fire]);

    const stop = useCallback(() => {
        clearTimer();
        setState({ kind: "idle" });
    }, []);

    const setSpeed = useCallback(
        (speed: PlaybackSpeed) => {
            setState((prev) => {
                if (prev.kind === "playing") {
                    clearTimer();
                    const intervalMs = BASE_INTERVAL_MS / speed;
                    const nextIndex = prev.sentIndex + 1;
                    if (nextIndex < prev.conversation.snippets.length) {
                        scheduleTimer(intervalMs, () => fire(prev.conversation, nextIndex, speed));
                    } else {
                        scheduleTimer(intervalMs, () => {
                            clearTimer();
                            setState({ kind: "idle" });
                        });
                    }
                    return { ...prev, speed };
                }
                if (prev.kind === "paused") {
                    return { ...prev, speed };
                }
                return prev;
            });
        },
        [fire],
    );

    return { state, play, pause, resume, stop, setSpeed };
}
